import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import HeroSlider from '../../components/HeroSlider';
import { motion } from 'framer-motion';
import { AlbumsService, Album } from '../../services/albums.service';
import { ArrowRight, Calendar, MapPin, ChevronRight, Activity, Award, Shield } from 'lucide-react';
import { FaImages, FaCalendarAlt } from 'react-icons/fa';
import logo from '../../assets/logo.png';
import { EvenementsService, Evenement } from '../../services/evenements.service';
import { getImageUrl } from '../../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Mock Data for Events (Événements)
const MOCK_EVENTS = [
  {
    id: 1,
    title: "Conférence Médicale Annuelle",
    date: { day: "15", month: "MARS" },
    location: "Hôtel Aurassi, Alger",
    description: "Une journée dédiée aux échanges entre professionnels de santé sur les avancées thérapeutiques."
  },
  {
    id: 2,
    title: "Journée Portes Ouvertes",
    date: { day: "22", month: "AVRIL" },
    location: "Usine SAPI, Blida",
    description: "Visite guidée de nos installations de production pour les étudiants en pharmacie."
  }
];

const Accueil: React.FC = () => {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loadingAlbums, setLoadingAlbums] = useState(true);

  const [news, setNews] = useState<Evenement[]>([]);

  useEffect(() => {
    loadAlbums();
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await EvenementsService.findAll(1, 100);
      let publishedEvents = Array.isArray(response) ? response : response.data;
      // Filter those published on home and slice the top 3
      publishedEvents = publishedEvents
        .filter(event => event.isPublishedOnHome)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 3);
      setNews(publishedEvents);
    } catch (error) {
      console.error("Failed to load news", error);
    }
  };

  const loadAlbums = async () => {
    try {
      const response = await AlbumsService.findAll(1, 4); // Fetch latest 4 albums
      setAlbums(response.data);
    } catch (error) {
      console.error("Failed to load albums", error);
    } finally {
      setLoadingAlbums(false);
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5 }
    })
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8, x: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number]
      }
    }
  };

  return (
    <div className="w-full bg-gray-10 min-h-screen">
      <Helmet>
        <title>SAPI - Société Algérienne de l'Industrie Pharmaceutique</title>
      </Helmet>

      {/* Hero Section */}
      <HeroSlider />

      {/* Presentation Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={sectionVariants}
        className="py-20 bg-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-1/3 h-full bg-primary-50 rounded-l-[100px] opacity-50 pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <span className="text-secondary font-bold tracking-wider uppercase text-sm mb-2 block">A PROPOS DE LA SAPI</span>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Présentation de la SAPI
              </h2>
              <div className="text-lg text-gray-600 leading-relaxed mb-6 space-y-4">
                <p className="font-semibold text-primary">Société Algérienne de Pharmacie Industrielle</p>
                <p>
                  La Société Algérienne de Pharmacie Industrielle est une association scientifique à but non lucratif créée en avril 2024. Elle a pour but d'unir et rassembler l'ensemble des universitaires, des chercheurs, pharmaciens, ingénieurs et industriels œuvrant dans le domaine de la pharmacie industrielle.
                </p>
                <p>
                  Elle constitue un espace de réflexion, de partenariat, d'innovation et de développement dédié à la mise en œuvre de solutions thérapeutiques concrètes pour la santé algérienne ainsi que pour la production pharmaceutique de qualité, durable et compétitive.
                </p>
              </div>

              <Link
                to="/Presentation"
                className="inline-flex items-center gap-2 mt-10 px-8 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary-dark transition-all shadow-lg hover:shadow-primary/30"
              >
                Découvrir SAPI <ArrowRight size={18} />
              </Link>
            </div>

            {/* Logo on the right with entrance animation */}
            <motion.div
              className="lg:w-1/2 flex items-center justify-center lg:justify-end"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={logoVariants}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/10 rounded-full blur-3xl transform scale-150 opacity-30" />
                <motion.img
                  src={logo}
                  alt="SAPI Logo"
                  className="relative z-10 h-64 w-auto lg:h-80 object-contain"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* News Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-primary font-bold tracking-wider uppercase text-sm mb-2 block">Actualités</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">Dernières Nouvelles</h2>
            </div>
            <Link to="/Evenement" className="hidden md:flex items-center gap-2 text-primary font-medium hover:text-primary-dark transition-colors">
              Voir tout <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {news.map((n, index) => (
              <motion.article
                key={n.id}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={cardVariants}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="h-56 overflow-hidden relative">
                  {n.image ? (
                    <img
                      src={getImageUrl(n.image)}
                      alt={n.titre}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <FaCalendarAlt className="text-gray-300 text-5xl" />
                    </div>
                  )}
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold text-primary uppercase shadow-lg">
                    Événement
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                    <Calendar size={14} className="text-primary" />
                    <span>{n.dateDebut ? format(new Date(n.dateDebut), 'dd/MM/yyyy') : ''}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300 line-clamp-2">
                    {n.titre}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                    {n.description || 'Aucune description.'}
                  </p>
                  <Link to={`/Evenement/${n.id}`} className="inline-flex items-center text-secondary font-semibold text-sm hover:text-secondary-dark transition-colors group/link">
                    Lire la suite <ChevronRight size={16} className="ml-1 transform group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link to="/Evenement" className="inline-flex items-center gap-2 text-primary font-medium hover:text-primary-dark transition-colors">
              Voir toutes les actualités <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Events & Gallery Split Section */}
      {/* <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

           
            <div className="lg:col-span-5">
              <span className="text-secondary font-bold tracking-wider uppercase text-sm mb-2 block">Agenda</span>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Prochains Événements</h2>

              <div className="space-y-6">
                {MOCK_EVENTS.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className="flex gap-6 group cursor-pointer"
                  >
                    <div className="flex-shrink-0 w-20 h-24 bg-primary/5 rounded-2xl flex flex-col items-center justify-center border border-primary/10 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                      <span className="text-2xl font-bold">{event.date.day}</span>
                      <span className="text-xs font-bold uppercase tracking-wide">{event.date.month}</span>
                    </div>
                    <div className="py-2">
                      <h4 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">{event.title}</h4>
                      <div className="flex items-center gap-2 text-gray-500 text-sm mt-1 mb-2">
                        <MapPin size={14} />
                        <span>{event.location}</span>
                      </div>
                      <p className="text-gray-600 text-sm text-sm line-clamp-2">{event.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Link to="/Evenement" className="inline-block mt-8 px-6 py-2 border border-gray-300 rounded-full text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-400 transition-all">
                Voir tout l'agenda
              </Link>
            </div>

           
            <div className="lg:col-span-7">
              <div className="flex justify-between items-end mb-8">
                <div>
                  <span className="text-accent font-bold tracking-wider uppercase text-sm mb-2 block">Médiathèque</span>
                  <h2 className="text-3xl font-bold text-gray-900">Galerie Photos</h2>
                </div>
                <Link to="/Galerie" className="text-sm font-medium text-gray-500 hover:text-accent transition-colors flex items-center gap-1">
                  Explorer <ArrowRight size={16} />
                </Link>
              </div>

              {loadingAlbums ? (
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-2xl">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {albums.length > 0 ? (
                    albums.map((album, index) => (
                      <motion.div
                        key={album.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className={`relative rounded-xl overflow-hidden cursor-pointer group h-48 ${index === 0 ? 'col-span-2 h-64' : ''}`}
                        onClick={() => navigate('/Galerie')}
                      >
                        {album.images && album.images.length > 0 ? (
                          <img
                            src={album.images[0].medium_url || album.images[0].original_url}
                            alt={album.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <FaImages className="text-gray-400 text-4xl" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute bottom-0 left-0 p-4 w-full">
                          <h4 className="text-white font-bold truncate text-lg group-hover:text-highlight transition-colors">{album.title}</h4>
                          <p className="text-white/80 text-xs mt-1">{album.images?.length || 0} photos</p>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                      <p className="text-gray-500">Aucun album disponible pour le moment.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </section> */}



    </div>
  );
};

export default Accueil;
